import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export type MiniDashboardTileKey = 'due' | 'overdue' | 'inProgress' | 'completed' | 'upcoming' | 'pending';

export interface TaskMiniDashboardCounts {
  dueCount?: number;
  overdueCount?: number;
  inProgressCount?: number;
  completedCount?: number;
  upcomingCount?: number;
  pendingCount?: number;
  totalTaskCount?: number;
}

/**
 * Normalizes task status to standard numeric / string status
 * 0: Pending, 1: In-Progress, 2: Completed, 3: Cancelled
 */
export const getNormalizedTaskStatus = (task: any): { isPending: boolean; isInProgress: boolean; isCompleted: boolean; isCancelled: boolean } => {
  const statusNum = task?.taskStatus !== undefined && task?.taskStatus !== null
    ? Number(task.taskStatus)
    : task?.statusEnum !== undefined && task?.statusEnum !== null
      ? Number(task.statusEnum)
      : null;

  const rawStatusName = String(task?.taskStatusName || task?.status || '').trim().toLowerCase().replace(/[^a-z]/g, '');

  const isPending = statusNum === 0 || rawStatusName === 'pending';
  const isInProgress = statusNum === 1 || rawStatusName === 'inprogress';
  const isCompleted = statusNum === 2 || rawStatusName === 'completed';
  const isCancelled = statusNum === 3 || rawStatusName === 'cancelled';

  return { isPending, isInProgress, isCompleted, isCancelled };
};

/**
 * Parse dueOn date to dayjs object at start of day
 */
export const parseTaskDueDate = (dueOn: any): dayjs.Dayjs | null => {
  if (!dueOn) return null;
  const rawStr = typeof dueOn === 'string' ? dueOn.replace(/Z$/i, '').replace(/[+-]\d{2}:\d{2}$/, '') : dueOn;
  const taskDate = dayjs(rawStr).startOf('day');
  return taskDate.isValid() ? taskDate : null;
};

/**
 * Checks if a task's due date is today.
 */
export const isDueDateToday = (dueOn: any): boolean => {
  const taskDate = parseTaskDueDate(dueOn);
  if (!taskDate) return false;
  const today = dayjs().startOf('day');
  return taskDate.isSame(today, 'day');
};

/**
 * Checks if a task's due date is before today (overdue).
 */
export const isDueDateOverdue = (dueOn: any): boolean => {
  const taskDate = parseTaskDueDate(dueOn);
  if (!taskDate) return false;
  const today = dayjs().startOf('day');
  return taskDate.isBefore(today, 'day');
};

/**
 * Checks if a task's due date is after today (upcoming).
 */
export const isDueDateUpcoming = (dueOn: any): boolean => {
  const taskDate = parseTaskDueDate(dueOn);
  if (!taskDate) return false;
  const today = dayjs().startOf('day');
  return taskDate.isAfter(today, 'day');
};

/**
 * Legacy check: Checks if a task's due date is today or before today.
 */
export const isDueDateTodayOrOverdue = (dueOn: any): boolean => {
  const taskDate = parseTaskDueDate(dueOn);
  if (!taskDate) return false;
  const today = dayjs().startOf('day');
  return taskDate.isSameOrBefore(today, 'day');
};

/**
 * Due Rule (PRD 2.2.2 & 8.1):
 * Status is Pending or In-Progress.
 * Due date is today (Due Date = Today).
 * Not Completed or Cancelled.
 */
export const isTaskDue = (task: any): boolean => {
  if (!task) return false;
  const { isPending, isInProgress, isCompleted, isCancelled } = getNormalizedTaskStatus(task);
  if (isCompleted || isCancelled) return false;
  if (!isPending && !isInProgress) return false;

  const dueOn = task?.dueOn || task?.dueOnISO || task?.dueDate;
  return isDueDateToday(dueOn);
};

/**
 * Overdue Rule:
 * Status is Pending or In-Progress.
 * Due date is before today (< Today).
 * Not Completed or Cancelled.
 */
export const isTaskOverdue = (task: any): boolean => {
  if (!task) return false;
  const { isPending, isInProgress, isCompleted, isCancelled } = getNormalizedTaskStatus(task);
  if (isCompleted || isCancelled) return false;
  if (!isPending && !isInProgress) return false;

  const dueOn = task?.dueOn || task?.dueOnISO || task?.dueDate;
  return isDueDateOverdue(dueOn);
};

/**
 * UpComing Rule:
 * Status is Pending.
 * Due date is after today (> Today).
 * Not Completed or Cancelled.
 */
export const isTaskUpcoming = (task: any): boolean => {
  if (!task) return false;
  const { isPending, isCompleted, isCancelled } = getNormalizedTaskStatus(task);
  if (!isPending || isCompleted || isCancelled) return false;

  const dueOn = task?.dueOn || task?.dueOnISO || task?.dueDate;
  return isDueDateUpcoming(dueOn);
};

/**
 * Pending Rule:
 * Status is Pending.
 * Not Completed or Cancelled.
 */
export const isTaskPending = (task: any): boolean => {
  if (!task) return false;
  const { isPending, isCompleted, isCancelled } = getNormalizedTaskStatus(task);
  if (!isPending || isCompleted || isCancelled) return false;

  const dueOn = task?.dueOn || task?.dueOnISO || task?.dueDate;
  if (!dueOn) return true;

  return isDueDateUpcoming(dueOn);
};

/**
 * In-Progress Rule:
 * Status is In-Progress.
 * Not Cancelled.
 */
export const isTaskInProgress = (task: any): boolean => {
  if (!task) return false;
  const { isInProgress, isCancelled } = getNormalizedTaskStatus(task);
  return isInProgress && !isCancelled;
};

/**
 * Completed Rule:
 * Status is Completed.
 * Not Cancelled.
 */
export const isTaskCompleted = (task: any): boolean => {
  if (!task) return false;
  const { isCompleted, isCancelled } = getNormalizedTaskStatus(task);
  return isCompleted && !isCancelled;
};

/**
 * Checks if a single task matches a specific dashboard tile key.
 */
export const matchesDashboardTile = (task: any, tileKey: MiniDashboardTileKey): boolean => {
  switch (tileKey) {
    case 'due':
      return isTaskDue(task);
    case 'overdue':
      return isTaskOverdue(task);
    case 'inProgress':
      return isTaskInProgress(task);
    case 'completed':
      return isTaskCompleted(task);
    case 'upcoming':
      return isTaskUpcoming(task);
    case 'pending':
      return isTaskPending(task);
    default:
      return false;
  }
};

/**
 * Filters a task collection by selected dashboard tiles using OR logic.
 * Ensures tasks matching multiple selected tiles are deduplicated.
 */
export const filterTasksByDashboardTiles = <T = any>(
  tasks: T[],
  selectedTiles: MiniDashboardTileKey[]
): T[] => {
  if (!Array.isArray(tasks) || tasks.length === 0) return [];
  if (!Array.isArray(selectedTiles) || selectedTiles.length === 0) return tasks;

  return tasks.filter((task) => {
    return selectedTiles.some((tileKey) => matchesDashboardTile(task, tileKey));
  });
};

/**
 * Calculates Mini Dashboard counts from a collection of tasks using PRD rules.
 */
export const calculateDashboardCounts = (tasks: any[]): TaskMiniDashboardCounts => {
  if (!Array.isArray(tasks) || tasks.length === 0) {
    return {
      dueCount: 0,
      overdueCount: 0,
      inProgressCount: 0,
      completedCount: 0,
      upcomingCount: 0,
      totalTaskCount: 0
    };
  }

  let dueCount = 0;
  let overdueCount = 0;
  let inProgressCount = 0;
  let completedCount = 0;
  let upcomingCount = 0;

  tasks.forEach((task) => {
    if (isTaskDue(task)) dueCount++;
    if (isTaskOverdue(task)) overdueCount++;
    if (isTaskInProgress(task)) inProgressCount++;
    if (isTaskCompleted(task)) completedCount++;
    if (isTaskUpcoming(task)) upcomingCount++;
  });

  return {
    dueCount,
    overdueCount,
    inProgressCount,
    completedCount,
    upcomingCount,
    totalTaskCount: tasks.length
  };
};
